import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowRight, Zap, Users, User, Flag } from "lucide-react";

export default function DoublesConfig() {
  const navigate = useNavigate();
  const { roundId } = useParams();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFoursomeDoubles, setSelectedFoursomeDoubles] = useState([]);
  const [selectedIndividualDoubles, setSelectedIndividualDoubles] = useState([]);

  useEffect(() => {
    fetchRound();
  }, [roundId]);

  const fetchRound = async () => {
    try {
      const response = await fetch(`${API}/rounds/${roundId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRound(data);
        
        // Pre-populate selected doubles from existing data
        const existingFoursomes = [];
        data.matchups?.forEach((m, i) => {
          if (m.is_doubled) existingFoursomes.push(i);
        });
        if (existingFoursomes.length > 0) setSelectedFoursomeDoubles(existingFoursomes);
        
        const existingIndividuals = [];
        data.individual_matchups?.forEach(m => {
          if (m.is_doubled) existingIndividuals.push(m.matchup_id);
        });
        if (existingIndividuals.length > 0) setSelectedIndividualDoubles(existingIndividuals);
      } else {
        toast.error("Ronda no encontrada");
        navigate("/dashboard");
      }
    } catch {
      
      toast.error("Error al cargar la ronda");
    } finally {
      setLoading(false);
    }
  };

  const toggleFoursomeDouble = (index) => {
    setSelectedFoursomeDoubles(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleIndividualDouble = (matchupId) => {
    setSelectedIndividualDoubles(prev => 
      prev.includes(matchupId)
        ? prev.filter(id => id !== matchupId)
        : [...prev, matchupId]
    );
  };

  const handleContinue = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/rounds/${roundId}/doubles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          foursome_doubles: selectedFoursomeDoubles,
          individual_doubles: selectedIndividualDoubles,
        }),
      });

      if (response.ok) {
        toast.success("Dobles configurados");
        navigate(`/round/${roundId}/play`);
      } else {
        throw new Error("Error setting doubles");
      }
    } catch {
      
      toast.error("Error al configurar dobles");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/rounds/${roundId}/doubles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          foursome_doubles: [],
          individual_doubles: [],
        }),
      });

      if (response.ok) {
        toast.success("Continuando sin dobles");
        navigate(`/round/${roundId}/play`);
      } else {
        throw new Error("Error");
      }
    } catch {
      
      toast.error("Error al continuar");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const basePlayers = round?.players?.filter(p => p.is_base) || [];

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="gradient-hero text-white">
        <div className="max-w-lg mx-auto px-4 py-6 text-center">
          <Zap className="w-12 h-12 text-[#D4A373] mx-auto mb-3" strokeWidth={1.5} />
          <h1 className="text-2xl font-bold mb-2">¡Primera Vuelta Completada!</h1>
          <p className="text-white/80 text-sm">{round?.name}</p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 -mt-4">
        {/* Info Card */}
        <Card className="card-default mb-6">
          <CardContent className="p-4">
            <p className="text-sm text-[#64748B]">
              Selecciona los matchups que deseas <strong>doblar</strong>. 
              Los puntos de la segunda vuelta valdrán el doble en los matchups seleccionados.
            </p>
          </CardContent>
        </Card>

        {/* Foursome Doubles */}
        <Card className="card-default mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1B4D3E]" />
              Foursome - Enfrentamientos de Parejas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {round?.matchups?.map((matchup, index) => {
              const secPlayers = matchup.secondary_pair?.map(id => 
                round.players.find(p => p.player_id === id)
              ) || [];
              const secNames = secPlayers.map(p => p?.name?.split(' ')[0]).join(' + ');
              const isLosing = matchup.total_points > 0;
              const isTied = matchup.total_points === 0;
              const isWinning = matchup.total_points < 0;

              return (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${
                    selectedFoursomeDoubles.includes(index)
                      ? 'border-[#D4A373] bg-[#D4A373]/10'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`foursome-${index}`}
                        checked={selectedFoursomeDoubles.includes(index)}
                        onCheckedChange={() => toggleFoursomeDouble(index)}
                        data-testid={`foursome-double-${index}`}
                      />
                      <div>
                        <p className="font-medium text-sm text-[#0F172A]">{secNames}</p>
                        <p className="text-xs text-[#64748B]">vs Base</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        matchup.total_points > 0 ? 'text-red-600' : 
                        matchup.total_points < 0 ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {matchup.total_points > 0 ? '-' : matchup.total_points < 0 ? '+' : ''}{Math.abs(matchup.total_points)}
                      </p>
                      <p className="text-xs text-[#64748B]">
                        {isLosing ? 'Sec perdiendo' : isTied ? 'Empatado' : 'Sec ganando'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Individual Doubles */}
        <Card className="card-default mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Individuales - Match Play
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {round?.individual_matchups?.map((matchup) => {
              const p1Winning = matchup.total_points > 0;
              const p2Winning = matchup.total_points < 0;
              const isTied = matchup.total_points === 0;

              return (
                <div 
                  key={matchup.matchup_id}
                  className={`p-3 rounded-lg border ${
                    selectedIndividualDoubles.includes(matchup.matchup_id)
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`individual-${matchup.matchup_id}`}
                        checked={selectedIndividualDoubles.includes(matchup.matchup_id)}
                        onCheckedChange={() => toggleIndividualDouble(matchup.matchup_id)}
                        data-testid={`individual-double-${matchup.matchup_id}`}
                      />
                      <div>
                        <p className="font-medium text-sm text-[#0F172A]">
                          {matchup.player1_name?.split(' ')[0]} vs {matchup.player2_name?.split(' ')[0]}
                        </p>
                        <p className="text-xs text-[#64748B]">
                          {p1Winning ? `${matchup.player1_name?.split(' ')[0]} gana` : 
                           p2Winning ? `${matchup.player2_name?.split(' ')[0]} gana` : 'Empatado'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        matchup.total_points > 0 ? 'text-green-600' : 
                        matchup.total_points < 0 ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {matchup.total_points > 0 ? '+' : ''}{matchup.total_points}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Summary */}
        {(selectedFoursomeDoubles.length > 0 || selectedIndividualDoubles.length > 0) && (
          <Card className="card-active mb-6">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-[#1B4D3E] mb-2">
                <Zap className="w-4 h-4 inline mr-1" />
                Matchups que irán al doble:
              </p>
              <ul className="text-sm text-[#64748B] space-y-1">
                {selectedFoursomeDoubles.map(i => {
                  const m = round?.matchups?.[i];
                  const secPlayers = m?.secondary_pair?.map(id => 
                    round.players.find(p => p.player_id === id)
                  ) || [];
                  return (
                    <li key={`fs-${i}`}>• Foursome: {secPlayers.map(p => p?.name?.split(' ')[0]).join(' + ')} vs Base</li>
                  );
                })}
                {selectedIndividualDoubles.map(id => {
                  const m = round?.individual_matchups?.find(m => m.matchup_id === id);
                  return (
                    <li key={`ind-${id}`}>• Individual: {m?.player1_name?.split(' ')[0]} vs {m?.player2_name?.split(' ')[0]}</li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={handleContinue}
            className="btn-primary w-full py-6"
            disabled={submitting}
            data-testid="confirm-doubles-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Guardando...
              </span>
            ) : (
              <>
                Confirmar y Continuar
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleSkip}
            className="w-full"
            disabled={submitting}
            data-testid="skip-doubles-btn"
          >
            Continuar sin doblar
          </Button>
        </div>
      </main>
    </div>
  );
}
